/**
 * Level builder: LevelDoc + game data -> SimGo hierarchy with rigidbodies, colliders, sprites,
 * tilemaps and behaviours, mirroring what the Unity importer / scene would contain at runtime.
 *
 * - Placed prefab objects are instantiated with their property modifications; every GameObject of
 *   the prefab becomes a SimGo, components become bodies / colliders / sprite state / behaviours.
 * - Tile layers become tilemap GameObjects. Solid layers get a static Rigidbody2D and a composite
 *   (outline) collider; spike and one-way layers get the generated trigger / effector children
 *   that SpikeTilemapGenerator / OneWayPlatformTilemapGenerator bake into Unity scenes.
 * - Bodies (moving platforms, fade-out blocks, rotating and controlled bodies) get one rigidbody on
 *   their content GameObject; their tile layers and riding objects are its children.
 */
import type { Body, CustomComponent, LevelDoc, PlacedObject, TileLayer } from '../../model/format.ts';
import { matrixFromFlags } from '../../model/format.ts';
import { containerTiles, terrainSurface } from '../../model/bake.ts';
import { spikeColliderRuns, spikeDests, type SpikeDir } from '../../editor/spikes.ts';
import type { TerrainContainer } from '../../terrain/index.ts';
import { instantiatePrefab, type InstGo, type InstNode, type PrefabInstance } from '../../data/prefabInstance.ts';
import { sortingLayerNameFromId, spriteFromRef, type GameData } from '../../data/gameData.ts';
import { IDENTITY, compose, invert, mul, type Xform } from '../../model/xform.ts';
import { BodyType, SimBody, SimCollider, SimGo, materialFromRef, type ColliderShape } from './world.ts';
import { CheckpointHandlerBehaviour, Game, GameBehaviour, type RefCtx } from './game.ts';
import { buildTileLoops, type Loop } from './tileCollision.ts';
import type { PixelSource } from './pixels.ts';
import { bool, list, num, rgba, vec } from './props.ts';
import type { V2 } from './unity.ts';
import { applyPlayerOverrides, PlayerController } from './player.ts';
import { REGISTRY, MovingPlatformHandler, SpikeHandler } from './mechanics/index.ts';
import { deactivatePlayerStrings, ensurePlayerStrings } from './mechanics/string.ts';
import { withGeneratedTiles } from '../../data/crumble.ts';

export const PLAYER_PREFAB = 'f7b170b9fdd2f214c88cad51f984bd06';
const BUILTIN_SQUARE_SPRITE = '311925a002f4447b3a28927169b83ea6';

export interface BuildOptions {
  doc: LevelDoc;
  gd: GameData;
  pixels: PixelSource;
  /** editor "play from here" spawn position */
  spawn?: [number, number] | null;
}

interface SpikeSet {
  floor: string | null;
  ceiling: string | null;
  left: string | null;
  right: string | null;
  height: number;
  shrink: number;
}

interface OneWaySettings {
  floor: Set<string>;
  ceiling: Set<string>;
  left: Set<string>;
  right: Set<string>;
  height: number;
  arc: number;
}

const COLLIDER_TYPES = new Set(['BoxCollider2D', 'CircleCollider2D', 'PolygonCollider2D', 'CapsuleCollider2D', 'EdgeCollider2D', 'TilemapCollider2D']);

/** Build the simulation for a level. The returned game has its initial (restart) snapshot taken. */
export function buildLevel(opts: BuildOptions): Game {
  return new LevelBuilder(opts).build();
}

class LevelBuilder {
  readonly game: Game;
  readonly doc: LevelDoc;
  readonly gd: GameData;
  readonly pixels: PixelSource;
  private spikeSets: SpikeSet[] = [];
  private spikeLayerIds = new Set<string>();
  private spikeBodyIds = new Set<string>();
  private oneWay: OneWaySettings | null = null;
  private objGos = new Map<string, Map<string, SimGo>>();
  private objCtx = new Map<string, RefCtx>();
  private objInst = new Map<string, PrefabInstance>();
  private owners = new Map<string, string[]>();

  constructor(o: BuildOptions) {
    this.doc = withGeneratedTiles(o.doc);
    this.gd = o.gd;
    this.pixels = o.pixels;
    this.game = new Game(this.doc, o.gd);
    this.game.pixels = o.pixels;
    this.game.spawnOverride = o.spawn ? { x: o.spawn[0], y: o.spawn[1] } : null;
  }

  build(): Game {
    const { doc, game } = this;
    for (const b of doc.bodies) if (b.owner) this.owners.set(b.owner.object, [...(this.owners.get(b.owner.object) ?? []), b.owner.handlerSourceId]);
    this.readLevelScripts();

    const top = containerTiles(doc, doc);
    for (const l of doc.layers) this.buildLayer(l, top.get(l.id) ?? l.tiles, null, null, true, undefined, doc);

    // objects: owners before attached children
    const pending = doc.objects.slice();
    const built = new Set<string>();
    for (let guard = 0; pending.length && guard < 20; guard++) {
      for (let i = 0; i < pending.length; ) {
        const o = pending[i];
        if (o.attach && !built.has(o.attach.object) && doc.objects.some((x) => x.id === o.attach!.object)) {
          i++;
          continue;
        }
        const parent = o.attach ? this.attachParent(o.attach) : null;
        this.buildObject(o, parent);
        built.add(o.id);
        pending.splice(i, 1);
      }
    }
    for (const o of pending) this.buildObject(o, null);

    for (const b of doc.bodies) this.buildBody(b);

    this.readLevelData();
    this.buildParallax();

    const ck = new SimGo(game, 'CheckpointHandler', null, IDENTITY, 'system:checkpointHandler');
    game.addBehaviour(new CheckpointHandlerBehaviour(ck));

    game.afterPlayersChanged = () => ensurePlayerStrings(game);
    game.onEnterDoor = (p) => deactivatePlayerStrings(game, p);

    game.playerOverrides = firstPlayerOverrides(game);
    game.spawnPlayerFactory = (index, color) => {
      const inst = instantiatePrefab(PLAYER_PREFAB, []);
      if (!inst) throw new Error('player prefab missing');
      const { root } = this.buildInstance(inst, null, IDENTITY, `player:${index}`, new Set(), null);
      root.name = `Player ${index + 1}`;
      const pc = root.getBehaviour(PlayerController)!;
      pc.index = index;
      pc.color = color;
      applyPlayerOverrides(pc, game.playerOverrides);
      pc.respawnFresh(game.getRespawnPoint(pc));
      return pc;
    };

    let spawned = 0;
    game.instantiateFactory = (guid, pos, rotation) => {
      const inst = instantiatePrefab(guid, []);
      if (!inst) return null;
      const l = inst.root.local;
      const sx = Math.hypot(l.a, l.b), det = l.a * l.d - l.b * l.c;
      const local = compose(pos.x, pos.y, rotation, sx || 1, det / (sx || 1) || 1);
      return this.buildInstance(inst, null, local, `spawn:${++spawned}`, new Set(), null).root;
    };

    game.captureInitial();
    game.settleSpawnOverride();
    return game;
  }

  // ---------- LevelScripts ----------

  private systemInstance(role: 'levelScripts' | 'camera' | 'necessary'): PrefabInstance | null {
    const id = this.doc.system[role];
    const o = id ? this.doc.objects.find((x) => x.id === id) : null;
    if (!o?.prefab) return null;
    return instantiatePrefab(o.prefab, o.mods);
  }

  private readLevelScripts() {
    const inst = this.systemInstance('levelScripts');
    if (!inst) return;
    for (const go of inst.gos) {
      for (const c of go.comps) {
        if (c.type === 'SpikeTilemapGenerator') {
          for (const s of list(c.data.spikeTileList)) {
            const g = (r: any) => (r?.guid ? String(r.guid) : null);
            this.spikeSets.push({ floor: g(s.floorTile), ceiling: g(s.ceilingTile), left: g(s.leftWallTile), right: g(s.rightWallTile), height: num(s.colliderHeight, 0.25), shrink: num(s.colliderEdgeShrink, 0.25) });
          }
          for (const t of list(c.data.tilemaps)) {
            const ent: string | undefined = t?.entity ?? t?.ref;
            if (!ent) continue;
            const base = ent.split('#')[0];
            if (base.startsWith('layer:')) this.spikeLayerIds.add(base.slice(6));
            else if (base.startsWith('body:')) this.spikeBodyIds.add(base.slice(5));
          }
        } else if (c.type === 'OneWayPlatformTilemapGenerator') {
          const s = (v: any) => new Set(list(v).map((r) => String(r?.guid ?? '')).filter(Boolean));
          this.oneWay = { floor: s(c.data.floorTiles), ceiling: s(c.data.ceilingTiles), left: s(c.data.leftWallTiles), right: s(c.data.rightWallTiles), height: num(c.data.colliderHeight, 0.25), arc: num(c.data.surfaceArcAngle, 30) };
        }
      }
    }
  }

  private readLevelData() {
    const id = this.doc.system.levelScripts;
    const go = id ? this.game.entities.get(`obj:${id}`) : null;
    const ld = go?.comps.get('LevelData');
    if (!ld) return;
    const m = this.game.levelMeta;
    m.connectPlayersWithString = bool(ld.connectPlayersWithString);
    m.maxStringLength = num(ld.maxStringLength, 8);
    m.checkpointObject = this.game.resolveRef(ld.checkpointActivator, this.objCtx.get(id!) ?? null)?.go ?? null;
  }

  private buildParallax() {
    const p = this.doc.settings.parallax;
    if (!p?.prefab) return;
    const inst = instantiatePrefab(p.prefab, p.mods);
    if (!inst) return;
    const { root } = this.buildInstance(inst, null, compose(p.x, p.y, 0), 'system:parallax', new Set(), null);
    this.game.parallaxRoot = root;
  }

  // ---------- objects ----------

  private attachParent(a: { object: string; path: string }): SimGo | null {
    const map = this.objGos.get(a.object);
    if (!map) return this.game.entities.get(`obj:${a.object}`) ?? null;
    const root = this.game.entities.get(`obj:${a.object}`) ?? null;
    if (!a.path || !root) return root;
    let g: SimGo | null = root;
    for (const name of a.path.split('/')) {
      g = g?.children.find((c) => c.name === name) ?? null;
      if (!g) return root;
    }
    return g;
  }

  private buildObject(o: PlacedObject, parent: SimGo | null): SimGo | null {
    const world = compose(o.x, o.y, o.rotation, o.scale[0], o.scale[1]);
    const local = parent ? mul(invert(parent.worldXform()), world) : world;
    let inst: PrefabInstance | null;
    let skipGos = new Set<InstGo>();
    let skipComp: ((n: InstNode) => boolean) | null = null;
    if (o.prefab) {
      inst = instantiatePrefab(o.prefab, o.mods);
      if (!inst) return null;
      const handlers = this.owners.get(o.id);
      if (handlers) {
        skipGos = ownedHandlerGos(inst, handlers);
        const hs = new Set(handlers.map((h) => inst!.nodes.get(h)?.go.id ?? h));
        skipComp = (n) => n.type === 'MovingPlatformHandler' && hs.has(n.go.id);
      }
    } else {
      inst = customInstance(o);
    }
    const { root, map, ctx } = this.buildInstance(inst, parent, local, `obj:${o.id}`, skipGos, skipComp, o.active === false);
    this.objGos.set(o.id, map);
    this.objCtx.set(o.id, ctx);
    this.objInst.set(o.id, inst);
    this.game.entities.set(`obj:${o.id}`, root);
    return root;
  }

  /** Create SimGos for a prefab instance (or custom object) and all their components. */
  buildInstance(
    inst: PrefabInstance,
    parent: SimGo | null,
    rootLocal: Xform,
    key: string,
    skipGos: Set<InstGo>,
    skipComp: ((n: InstNode) => boolean) | null,
    rootInactive = false,
  ): { root: SimGo; map: Map<string, SimGo>; ctx: RefCtx } {
    const game = this.game;
    const ctx: RefCtx = new Map();
    const byInst = new Map<InstGo, SimGo>();
    const map = new Map<string, SimGo>();
    for (const ig of inst.gos) {
      if (skipGos.has(ig)) continue;
      const p = ig.parent ? byInst.get(ig.parent) : parent;
      if (ig.parent && !p) continue;
      const goData = inst.def.nodes[ig.id]?.data ?? inst.nodes.get(ig.id)?.data ?? {};
      const g = new SimGo(game, ig.name, p ?? null, ig === inst.root ? rootLocal : ig.local, ig === inst.root ? key : `${key}/${ig.id}`);
      if (goData.m_Layer !== undefined) g.layer = num(goData.m_Layer);
      if (goData.m_TagString) g.tag = String(goData.m_TagString);
      const active = ig.activeSelf && !(ig === inst.root && rootInactive);
      if (!active) g.setActive(false);
      byInst.set(ig, g);
      map.set(ig.id, g);
      ctx.set(ig.id, { go: g, type: 'GameObject', obj: g });
      for (const c of ig.comps) {
        if (!g.comps.has(c.type)) g.comps.set(c.type, c.data);
        if (c.type === 'Transform' || c.type === 'RectTransform') ctx.set(c.id, { go: g, type: 'Transform', obj: g });
        else ctx.set(c.id, { go: g, type: c.type, obj: g });
      }
    }
    // rigidbodies first: colliders attach to the nearest one
    for (const [ig, g] of byInst) {
      const rb = ig.comps.find((c) => c.type === 'Rigidbody2D');
      if (!rb) continue;
      this.makeBody(g, rb.data);
      ctx.set(rb.id, { go: g, type: 'Rigidbody2D', obj: g.body });
    }
    for (const [ig, g] of byInst) {
      for (const c of ig.comps) {
        if (!COLLIDER_TYPES.has(c.type)) continue;
        const col = this.makeCollider(g, c.type, c.data, ig);
        if (col) ctx.set(c.id, { go: g, type: c.type, obj: col });
      }
      const eff = ig.comps.find((c) => c.type === 'PlatformEffector2D');
      if (eff) {
        const e = { arc: num(eff.data.m_SurfaceArc, 180), useOneWay: bool(eff.data.m_UseOneWay, true) };
        for (const col of g.colliders) if (col.usedByEffector) col.effector = e;
      }
      for (const col of g.colliders) col.sync();
      for (const c of ig.comps) {
        if (c.type === 'SpriteRenderer') this.makeSprite(g, c.data);
        else if (c.type === 'Tilemap') {
          const r = ig.comps.find((x) => x.type === 'TilemapRenderer');
          g.tilemap = {
            key: `${key}:${c.id}`,
            tiles: c.data.tiles ?? [],
            palette: c.data.palette ?? [],
            color: rgba(c.data.m_Color),
            sortingLayer: sortingLayerNameFromId(r?.data?.m_SortingLayerID),
            order: num(r?.data?.m_SortingOrder),
            enabled: !r || bool(r.data.m_Enabled, true),
          };
        }
      }
    }
    const created: GameBehaviour[] = [];
    for (const [ig, g] of byInst) {
      for (const c of ig.comps) {
        if (skipComp?.(c)) continue;
        const b = this.makeBehaviour(g, c.type, c.data, ctx);
        if (!b) continue;
        ctx.set(c.id, { go: g, type: c.type, obj: b });
        created.push(b);
      }
    }
    for (const b of created) game.addBehaviour(b);
    const root = byInst.get(inst.root)!;
    return { root, map, ctx };
  }

  private makeBehaviour(g: SimGo, type: string, data: any, ctx: RefCtx | null): GameBehaviour | null {
    const Cls = REGISTRY[type];
    if (!Cls) return null;
    const b = new Cls(g, data ?? {});
    b.unityType = type;
    b.ctx = ctx;
    b.enabled = bool(data?.m_Enabled, true);
    return b;
  }

  private addComponents(g: SimGo, comps: CustomComponent[]) {
    const created: GameBehaviour[] = [];
    for (const c of comps) {
      if (!g.comps.has(c.type)) g.comps.set(c.type, c.data);
      const b = this.makeBehaviour(g, c.type, c.data, null);
      if (b) created.push(b);
    }
    for (const b of created) this.game.addBehaviour(b);
  }

  // ---------- components ----------

  makeBody(g: SimGo, d: any, override?: BodyType) {
    const type = override ?? (num(d.m_BodyType) as BodyType);
    const cons = num(d.m_Constraints);
    g.captureBodyScale();
    const b = new SimBody(g, type, {
      mass: num(d.m_Mass, 1),
      useAutoMass: bool(d.m_UseAutoMass),
      gravityScale: num(d.m_GravityScale, 1),
      linearDamping: num(d.m_LinearDamping ?? d.m_LinearDrag, 0),
      angularDamping: num(d.m_AngularDamping ?? d.m_AngularDrag, 0.05),
      freezeRotation: (cons & 4) !== 0,
      freezeX: (cons & 1) !== 0,
      freezeY: (cons & 2) !== 0,
      continuous: num(d.m_CollisionDetection) === 1,
      sleep: num(d.m_SleepingMode, 1) !== 0,
    });
    b.material = materialFromRef(d.m_Material);
    g.body = b;
    g.markDirty();
    if (!bool(d.m_Simulated, true)) b.setSimulated(false);
    b.syncActive();
  }

  private makeCollider(g: SimGo, type: string, d: any, ig: InstGo | null): SimCollider | null {
    const off = vec(d.m_Offset);
    let shape: ColliderShape | null = null;
    switch (type) {
      case 'BoxCollider2D': {
        const s = vec(d.m_Size, { x: 1, y: 1 });
        shape = { kind: 'box', w: s.x, h: s.y, ox: off.x, oy: off.y, edge: num(d.m_EdgeRadius) };
        break;
      }
      case 'CircleCollider2D':
        shape = { kind: 'circle', r: num(d.m_Radius, 0.5), ox: off.x, oy: off.y };
        break;
      case 'CapsuleCollider2D': {
        const s = vec(d.m_Size, { x: 0.5, y: 1 });
        shape = { kind: 'capsule', w: s.x, h: s.y, ox: off.x, oy: off.y, dir: num(d.m_Direction) };
        break;
      }
      case 'PolygonCollider2D': {
        const paths: Loop[] = list(d.m_Points?.m_Paths).map((p: any) => list(p).map((v: any) => [num(v.x), num(v.y)] as [number, number]));
        shape = { kind: 'polygon', paths, ox: off.x, oy: off.y };
        break;
      }
      case 'EdgeCollider2D': {
        const pts: Loop = list(d.m_Points).map((v: any) => [num(v.x), num(v.y)] as [number, number]);
        shape = { kind: 'edge', points: pts, ox: off.x, oy: off.y, edge: num(d.m_EdgeRadius) };
        break;
      }
      case 'TilemapCollider2D': {
        const tm = ig?.comps.find((c) => c.type === 'Tilemap');
        if (!tm) return null;
        const loops = buildTileLoops(tm.data.tiles ?? [], { palette: tm.data.palette ?? [], gd: this.gd, pixels: this.pixels });
        shape = { kind: 'chains', loops };
        break;
      }
    }
    if (!shape) return null;
    const col = new SimCollider(g, type, shape, {
      isTrigger: bool(d.m_IsTrigger),
      enabled: bool(d.m_Enabled, true),
      material: materialFromRef(d.m_Material),
      density: num(d.m_Density, 1),
      usedByEffector: bool(d.m_UsedByEffector),
    });
    g.colliders.push(col);
    return col;
  }

  private makeSprite(g: SimGo, d: any) {
    g.sprite = {
      sprite: spriteFromRef(d.m_Sprite),
      color: rgba(d.m_Color),
      flipX: bool(d.m_FlipX),
      flipY: bool(d.m_FlipY),
      enabled: bool(d.m_Enabled, true),
      sortingLayer: sortingLayerNameFromId(d.m_SortingLayerID),
      order: num(d.m_SortingOrder),
      drawMode: num(d.m_DrawMode),
      size: [num(d.m_Size?.x, 1), num(d.m_Size?.y, 1)],
      tileMode: num(d.m_SpriteTileMode),
      tileThreshold: num(d.m_AdaptiveModeThreshold, 0.5),
      maskInteraction: num(d.m_MaskInteraction),
    };
    if (d.m_Sprite?.guid === BUILTIN_SQUARE_SPRITE) g.sprite.square = true;
  }

  // ---------- tile layers ----------

  private buildLayer(l: TileLayer, tiles: number[], parent: SimGo | null, bodyId: string | null, staticBody: boolean, skipExtra?: Set<string>, container?: TerrainContainer): SimGo {
    const game = this.game;
    const local = compose(l.offset?.[0] ?? 0, l.offset?.[1] ?? 0, 0);
    const g = new SimGo(game, l.name, parent, local, `layer:${l.id}`);
    g.tilemap = {
      key: l.id,
      tiles,
      palette: this.doc.palette,
      color: (l.color ?? [1, 1, 1, 1]).slice() as [number, number, number, number],
      sortingLayer: l.sortingLayer,
      order: l.sortingOrder,
      enabled: true,
    };
    const solid = (l.role === 'level' || l.role === 'oneway') && l.collider !== false;
    if (solid) {
      if (staticBody && !g.attachedBody()) this.makeBody(g, {}, BodyType.Static);
      const loops = buildTileLoops(tiles, { palette: this.doc.palette, gd: this.gd, pixels: this.pixels });
      if (loops.length) {
        const col = new SimCollider(g, 'CompositeCollider2D', { kind: 'chains', loops });
        g.colliders.push(col);
        col.sync();
      }
    }
    const spikes = l.spikeGenerator || l.role === 'spikes' || this.spikeLayerIds.has(l.id) || (bodyId !== null && this.spikeBodyIds.has(bodyId) && solid);
    const skipSpikeCells = new Set<number>();
    if (l.role === 'spikes' && container?.spikeLines?.length) {
      const dests = spikeDests(container, terrainSurface(this.doc, container));
      this.generateSpikesFromLines(g, dests);
      for (const d of dests) skipSpikeCells.add(key(d.x, d.y));
    }
    if (spikes && this.spikeSets.length) this.generateSpikes(g, tiles, skipSpikeCells);
    if ((l.oneWayGenerator || l.role === 'oneway') && this.oneWay) this.generateOneWay(g, tiles);
    const extra = (l.extraComponents ?? []).filter((c) => !skipExtra?.has(c.type));
    if (extra.length) this.addComponents(g, extra);
    game.entities.set(`layer:${l.id}`, g);
    return g;
  }

  private addSpikeCollider(tm: SimGo, t: 0 | 1 | 2 | 3, sx: number, sy: number, ex: number, ey: number, height: number, shrink: number) {
    const g = new SimGo(this.game, 'SpikeCollider', tm, compose((sx + ex) / 2 + 0.5, (sy + ey) / 2 + 0.5, 0));
    const horiz = t === 0 || t === 1;
    let w: number, h: number;
    if (horiz) {
      w = ex - sx + 1 - shrink * 2;
      h = height;
    } else {
      w = height;
      h = ey - sy + 1 - shrink * 2;
    }
    let ox = 0, oy = 0;
    if (t === 0) oy -= (1 - h) / 2;
    if (t === 1) oy += (1 - h) / 2;
    if (t === 2) ox -= (1 - w) / 2;
    if (t === 3) ox += (1 - w) / 2;
    const col = new SimCollider(g, 'BoxCollider2D', { kind: 'box', w, h, ox, oy, edge: 0 }, { isTrigger: true });
    g.colliders.push(col);
    col.sync();
    const b = new SpikeHandler(g, {});
    b.unityType = 'SpikeHandler';
    this.game.addBehaviour(b);
  }

  /** Line-painted spikes: one collider run per dest direction, including both sides of an inside corner. */
  private generateSpikesFromLines(tm: SimGo, dests: ReturnType<typeof spikeDests>) {
    if (!dests.length) return;
    const height = this.spikeSets[0]?.height ?? 0.25;
    const shrink = this.spikeSets[0]?.shrink ?? 0.25;
    const st: Record<SpikeDir, 0 | 1 | 2 | 3> = { floor: 0, ceiling: 1, left: 2, right: 3 };
    for (const run of spikeColliderRuns(dests)) this.addSpikeCollider(tm, st[run.dir], run.x0, run.y0, run.x1, run.y1, height, shrink);
  }

  /** SpikeTilemapGenerator.GenerateColliders */
  private generateSpikes(tm: SimGo, tiles: number[], skip = new Set<number>()) {
    const cells = cellMap(tiles);
    if (!cells.size) return;
    const pal = this.doc.palette;
    const tileGuid = (k: number) => {
      const c = cells.get(k);
      return c ? pal[c.p]?.tile ?? null : null;
    };
    type ST = 0 | 1 | 2 | 3; // Floor, Ceiling, Left, Right
    const spikeType = (k: number, set: SpikeSet): ST | null => {
      const c = cells.get(k);
      const guid = tileGuid(k);
      if (!c || !guid) return null;
      let n: V2 | null = null;
      if (guid === set.floor) n = { x: 0, y: 1 };
      if (guid === set.ceiling) n = { x: 0, y: -1 };
      if (guid === set.left) n = { x: 1, y: 0 };
      if (guid === set.right) n = { x: -1, y: 0 };
      if (!n) return null;
      return classify(c.f, n);
    };
    const { x0, y0, x1, y1 } = bounds(tiles);
    const handled = new Set<number>(skip);
    for (let y = y0; y < y1; y++) {
      for (let x = x0; x < x1; x++) {
        const k = key(x, y);
        if (!cells.has(k) || handled.has(k)) continue;
        handled.add(k);
        for (const set of this.spikeSets) {
          const t = spikeType(k, set);
          if (t === null) continue;
          const horiz = t === 0 || t === 1;
          const walk = (dx: number, dy: number) => {
            let px = x, py = y;
            for (;;) {
              const nk = key(px + dx, py + dy);
              if (spikeType(nk, set) !== t) break;
              handled.add(nk);
              px += dx;
              py += dy;
            }
            return [px, py];
          };
          const [sx, sy] = horiz ? walk(-1, 0) : walk(0, -1);
          const [ex, ey] = horiz ? walk(1, 0) : walk(0, 1);
          this.addSpikeCollider(tm, t, sx, sy, ex, ey, set.height, set.shrink);
        }
      }
    }
  }

  /** OneWayPlatformTilemapGenerator.GenerateColliders (two stacked effector boxes per run) */
  private generateOneWay(tm: SimGo, tiles: number[]) {
    const ow = this.oneWay!;
    const cells = cellMap(tiles);
    if (!cells.size) return;
    const pal = this.doc.palette;
    const platformType = (k: number): number | null => {
      const c = cells.get(k);
      const guid = c ? pal[c.p]?.tile : null;
      if (!c || !guid) return null;
      let n: V2 | null = null;
      if (ow.floor.has(guid)) n = { x: 0, y: 1 };
      if (ow.ceiling.has(guid)) n = { x: 0, y: -1 };
      if (ow.left.has(guid)) n = { x: 1, y: 0 };
      if (ow.right.has(guid)) n = { x: -1, y: 0 };
      if (!n) return null;
      return classify(c.f, n);
    };
    const { x0, y0, x1, y1 } = bounds(tiles);
    const handled = new Set<number>();
    for (let y = y0; y < y1; y++) {
      for (let x = x0; x < x1; x++) {
        const k = key(x, y);
        if (!cells.has(k) || handled.has(k)) continue;
        handled.add(k);
        const t = platformType(k);
        if (t === null) continue;
        const horiz = t === 0 || t === 1;
        const walk = (dx: number, dy: number) => {
          let px = x, py = y;
          for (;;) {
            const nk = key(px + dx, py + dy);
            if (platformType(nk) !== t) break;
            handled.add(nk);
            px += dx;
            py += dy;
          }
          return [px, py];
        };
        const [sx, sy] = horiz ? walk(-1, 0) : walk(0, -1);
        const [ex, ey] = horiz ? walk(1, 0) : walk(0, 1);
        for (const posOffset of [0, 0.01]) {
          const g = new SimGo(this.game, 'OneWayCollider', tm, compose((sx + ex) / 2 + 0.5, (sy + ey) / 2 + 0.5, 0));
          const eff = { arc: ow.arc, useOneWay: true };
          g.comps.set('PlatformEffector2D', { m_SurfaceArc: String(ow.arc), m_UseOneWay: '1' });
          const w = horiz ? ex - sx + 1 : ow.height;
          const h = horiz ? ow.height : ey - sy + 1;
          let ox = 0, oy = 0;
          if (t === 0) oy += (1 - h) / 2 - posOffset;
          if (t === 1) oy -= (1 - h) / 2 - posOffset;
          if (t === 2) ox += (1 - w) / 2 - posOffset;
          if (t === 3) ox -= (1 - w) / 2 - posOffset;
          const col = new SimCollider(g, 'BoxCollider2D', { kind: 'box', w, h, ox, oy, edge: 0 }, { usedByEffector: true });
          col.effector = eff;
          g.colliders.push(col);
          col.sync();
        }
      }
    }
  }

  // ---------- bodies ----------

  private buildBody(b: Body) {
    const game = this.game;
    const tiles = containerTiles(this.doc, b);
    const rbType = b.rigidbody?.bodyType ?? 1;
    const contentWorld = compose(b.origin[0], b.origin[1], b.rotation ?? 0);
    let content: SimGo;
    let root: SimGo;
    const bodyComps: CustomComponent[] = [];
    if (b.behavior === 'movingPlatform') {
      let handler: SimGo | null = null;
      if (b.owner) {
        const hid = this.objInst.get(b.owner.object)?.nodes.get(b.owner.handlerSourceId)?.go.id ?? b.owner.handlerSourceId;
        handler = this.objGos.get(b.owner.object)?.get(hid) ?? null;
      }
      if (!handler) {
        const hp = b.handlerPosition ?? b.origin;
        handler = new SimGo(game, b.name, null, compose(hp[0], hp[1], 0), `body:${b.id}/handler`);
      }
      content = new SimGo(game, 'MovingPlatform', handler, mul(invert(handler.worldXform()), contentWorld), `body:${b.id}`);
      this.makeBody(content, {}, BodyType.Kinematic);
      root = handler;
      const mp = new MovingPlatformHandler(handler, b.moving ?? null);
      mp.unityType = 'MovingPlatformHandler';
      mp.contentGo = content;
      if (b.owner) {
        const inst = this.objInst.get(b.owner.object);
        const ctx = this.objCtx.get(b.owner.object);
        const node = inst?.nodes.get(b.owner.handlerSourceId)?.go.comps.find((c) => c.type === 'MovingPlatformHandler');
        if (node && ctx) ctx.set(node.id, { go: handler, type: 'MovingPlatformHandler', obj: mp });
        mp.ctx = ctx ?? null;
      }
      game.addBehaviour(mp);
    } else {
      content = new SimGo(game, b.name, null, contentWorld, `body:${b.id}`);
      const rbData = b.components?.find((c) => c.type === 'Rigidbody2D')?.data ?? {};
      this.makeBody(content, rbData, rbType === 2 ? BodyType.Static : rbType === 0 ? BodyType.Dynamic : BodyType.Kinematic);
      root = content;
      const seen = new Set<string>();
      for (const c of b.components ?? []) {
        if (c.type === 'Rigidbody2D' || seen.has(c.type)) continue;
        seen.add(c.type);
        bodyComps.push(c);
      }
      for (const l of b.layers) for (const c of l.extraComponents ?? []) if (!seen.has(c.type)) {
        seen.add(c.type);
        bodyComps.push(c);
      }
    }
    game.entities.set(`body:${b.id}`, content);
    if (root !== content) game.entityRoots.set(`body:${b.id}`, root);
    const skip = new Set(bodyComps.map((c) => c.type));
    for (const l of b.layers) this.buildLayer(l, tiles.get(l.id) ?? l.tiles, content, b.id, false, b.behavior === 'movingPlatform' ? undefined : skip, b);
    if (bodyComps.length) this.addComponents(content, bodyComps);
    const inBody = (o: PlacedObject) => !!o.attach && b.objects.some((x) => x.id === o.attach!.object);
    for (const o of b.objects) if (!inBody(o)) this.buildObject(o, content);
    for (const o of b.objects) if (inBody(o)) this.buildObject(o, this.attachParent(o.attach!) ?? content);
  }
}

// ---------- helpers ----------

const key = (x: number, y: number) => (x + 32768) * 65536 + (y + 32768);

function cellMap(tiles: number[]): Map<number, { p: number; f: number }> {
  const m = new Map<number, { p: number; f: number }>();
  for (let i = 0; i < tiles.length; i += 4) if (tiles[i + 2] >= 0) m.set(key(tiles[i], tiles[i + 1]), { p: tiles[i + 2], f: tiles[i + 3] });
  return m;
}

function bounds(tiles: number[]) {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (let i = 0; i < tiles.length; i += 4) {
    x0 = Math.min(x0, tiles[i]);
    y0 = Math.min(y0, tiles[i + 1]);
    x1 = Math.max(x1, tiles[i] + 1);
    y1 = Math.max(y1, tiles[i + 1] + 1);
  }
  return { x0, y0, x1, y1 };
}

/** rotate/flip a base normal by the tile matrix and classify: 0 floor, 1 ceiling, 2 left wall, 3 right wall */
function classify(flags: number, n: V2): 0 | 1 | 2 | 3 {
  const [a, b, c, d] = matrixFromFlags(flags);
  const rx = a * n.x + b * n.y, ry = c * n.x + d * n.y;
  if (Math.abs(rx) > Math.abs(ry)) return rx >= 0 ? 2 : 3;
  return ry >= 0 ? 0 : 1;
}

/** GameObjects inside an instance that belong to an owned body (door halves) and are built by the body. */
function ownedHandlerGos(inst: PrefabInstance, handlerIds: string[]): Set<InstGo> {
  const out = new Set<InstGo>();
  for (const hid of handlerIds) {
    const n = inst.nodes.get(hid);
    if (!n) continue;
    const walk = (g: InstGo) => {
      if (g.name.startsWith('MoveTo')) return;
      out.add(g);
      g.children.forEach(walk);
    };
    n.go.children.forEach(walk);
  }
  return out;
}

/** A custom (component-only) placed object as a one-GameObject instance. */
/** First active PlayerOverrides, in the order objects were built (Unity FindFirstObjectByType). */
function firstPlayerOverrides(game: Game): Record<string, unknown> | null {
  for (const g of game.gos) {
    if (!g.active || g.destroyed) continue;
    const d = g.comps.get('PlayerOverrides') as Record<string, unknown> | undefined;
    if (!d || String(d.m_Enabled ?? '1') === '0') continue;
    return d;
  }
  return null;
}

function customInstance(o: PlacedObject): PrefabInstance {
  const go: InstGo = { id: 'root', name: o.name, parent: null, children: [], activeSelf: true, active: true, comps: [], transform: null, local: IDENTITY, world: IDENTITY };
  const nodes = new Map<string, InstNode>();
  (o.components ?? []).forEach((c, i) => {
    const n: InstNode = { id: `c${i}`, type: c.type, data: c.data, go };
    go.comps.push(n);
    nodes.set(n.id, n);
  });
  nodes.set('root', { id: 'root', type: 'GameObject', data: {}, go });
  return { def: { guid: '', name: o.name, path: '', gos: [], nodes: {}, alias: {} }, gos: [go], nodes, root: go };
}
